<template>
    <search-form-layout :popup="popup" title="transfers.transfers" @close-popup="$emit('close-popup')">
        <template v-slot:form>
            <v-form ref="form" v-model="valid">
                <v-row>
                    <v-col md="6" cols="12" class="d-md-flex justify-center justify-md-space-between">
                        <transfer-point-autocomplete
                            ref="firstLocation"
                            v-model="departurePoint"
                            :rules="[v => !!v || `${$t('transfers.departure_location')} ${$t('validation.required')}`]"
                            label="transfers.departure_location"
                            single-line
                            filled
                            class="flex-grow-1 location-field"
                        >
                            <template
                                v-if="checkVisibilitySpecifyExactAddressDialog(departurePoint)"
                                v-slot:append-inner
                            >
                                <v-icon
                                    icon="mdi-map-outline"
                                    class="cursor-pointer"
                                    @mousedown.stop
                                    @click.stop="departureMapIsVisible = true"
                                />
                                <specify-exact-address-modal
                                    v-if="departureMapIsVisible"
                                    v-model="departurePoint"
                                    @closed="departureMapIsVisible = false"
                                />
                            </template>
                        </transfer-point-autocomplete>
                        <div class="text-center swap-btn">
                            <v-btn
                                :icon="isMobile ? 'mdi-swap-vertical-bold' : 'mdi-swap-horizontal-bold'"
                                variant="text"
                                @click="swapLocations"
                            />
                        </div>
                        <transfer-point-autocomplete
                            ref="secondLocation"
                            v-model="arrivalPoint"
                            :rules="[v => !!v || `${$t('transfers.arrival_location')} ${$t('validation.required')}`]"
                            :departure-location="departurePoint"
                            label="transfers.arrival_location"
                            single-line
                            filled
                            class="flex-grow-1 location-field"
                        >
                            <template v-if="checkVisibilitySpecifyExactAddressDialog(arrivalPoint)" v-slot:append-inner>
                                <v-icon
                                    icon="mdi-map-outline"
                                    class="cursor-pointer"
                                    @mousedown.stop
                                    @click.stop="arrivalMapIsVisible = true"
                                />
                                <specify-exact-address-modal
                                    v-if="arrivalMapIsVisible"
                                    v-model="arrivalPoint"
                                    @closed="arrivalMapIsVisible = false"
                                />
                            </template>
                        </transfer-point-autocomplete>
                    </v-col>
                    <v-col md="2" cols="12">
                        <vc-date-picker
                            v-model="dateTimeData"
                            :locale="$i18n.locale"
                            :min-date="minDateTime"
                            is-required
                        >
                            <template v-slot="{inputEvents}">
                                <div v-on="inputEvents">
                                    <v-text-field
                                        :class="{caption: $breakpoint.smAndDown}"
                                        :label="$t('transfers.departure_date')"
                                        readonly
                                        single-line
                                        variant="outlined"
                                        hide-details
                                        bg-color="white"
                                        prepend-inner-icon="mdi-calendar"
                                        :model-value="formattedDate"
                                    />
                                </div>
                            </template>
                        </vc-date-picker>
                    </v-col>
                    <v-col md="2" cols="12">
                        <tourists-select-menu
                            :_adults="adults"
                            :_children-ages="childrenAges"
                            token="passenger"
                            :max-total="50"
                            @change-adults="adults = $event"
                            @change-children-ages="childrenAges = $event"
                        />
                    </v-col>
                    <v-col md="2" cols="12" class="hidden-sm-and-down">
                        <v-btn block size="x-large" color="primary" :disabled="!valid" @click="search">
                            {{ $t('search') }}
                        </v-btn>
                    </v-col>
                </v-row>
            </v-form>
            <additional-options-wrapper
                ref="addForm"
                :suppliers="suppliers"
                :options="[
                    'transfer-type',
                    'promocode',
                    'citizenship',
                    'internalSuppliers',
                    'externalSuppliers',
                    'supplierId',
                ]"
            >
                <template v-slot:customOptions>
                    <custom-additional-options ref="additionalOptions" product-name="transfer" />
                </template>
            </additional-options-wrapper>
            <v-row class="hidden-md-and-up">
                <v-col md="2" cols="12">
                    <v-btn block size="x-large" color="primary" :disabled="!valid" @click="search">
                        {{ $t('search') }}
                    </v-btn>
                </v-col>
            </v-row>
        </template>
    </search-form-layout>
</template>

<script lang="ts">
    import {Component, Prop, mixins} from 'vue-facing-decorator'
    import TransferPointAutocomplete from '~src/components/transfers/search/forms/TransferPointAutocomplete.vue'
    import {AdditionalSearchOptionsMixin} from '~/mixins/AdditionalSearchOptionsMixin.vue'
    import AdditionalOptionsWrapper from '~src/components/snippets/AdditionalSearchOptions/additionalOptionsWrapper.src'
    import SearchFormLayout from '~/components/parts/SearchFormLayout.vue'
    import TouristsSelectMenu from '~src/components/search/forms/touristsSelectMenu.src'
    import {CHANGE_TAB_MOBILE_EVENT, EventBus} from '@/utils/event-bus'
    import {appInstance} from '@/utils/app-accessor'
    import CustomAdditionalOptions from '~src/components/snippets/AdditionalSearchOptions/CustomAdditionalOptions.vue'
    import SpecifyExactAddressModal from '~/components/transfers/snippets/SpecifyExactAddressModal.vue'

    @Component({
        components: {
            SpecifyExactAddressModal,
            CustomAdditionalOptions,
            TouristsSelectMenu,
            TransferPointAutocomplete,
            AdditionalOptionsWrapper,
            SearchFormLayout,
        },
    })
    export class TransferSearchForm extends mixins(AdditionalSearchOptionsMixin) {
        @Prop({default: false}) popup

        valid = false
        departurePoint = null
        departureMapIsVisible = false
        arrivalPoint = null
        arrivalMapIsVisible = false
        //TODO Add time picker
        dateTime = null
        adults = 2
        childrenAges = []
        suppliers = []

        //TODO Add CHANGE_LOCALE_EVENT handler

        async mounted() {
            await this.$store.restored
            this.arrivalPoint =
                transfersStore.searchRequest.arrivalCityId && Object.keys(transfersRuntimeStore.arrivalPoint).length
                    ? transfersRuntimeStore.arrivalPoint
                    : null
            this.departurePoint =
                transfersStore.searchRequest.departureCityId && Object.keys(transfersRuntimeStore.departurePoint).length
                    ? transfersRuntimeStore.departurePoint
                    : null
            if (this.$dateFns.isAfter(this.$dateFns.parseISO(transfersStore.searchRequest.dateTime), new Date())) {
                this.dateTime = transfersStore.searchRequest.dateTime
            } else {
                this.dateTime = this.$dateFns.format(this.$dateFns.addDays(new Date(), 1))
            }
            this.adults = authStore.user.isEmployeeOrExternalCorp ? 1 : transfersStore.searchRequest.adults
            this.childrenAges = authStore.user.isEmployeeOrExternalCorp
                ? []
                : [...transfersStore.searchRequest.childrenAges]
            if (this.isTO1) {
                try {
                    this.suppliers = (await this.$api.suppliers.get({productType: 'transfer'}))?.suppliers
                    // eslint-disable-next-line no-empty
                } catch (e) {}
            }
        }

        newQuery() {
            const query = {
                dateTime: this.dateTime,
                adults: this.adults,
                childrenAges: this.childrenAges,
                convertToCurrency: persistentStore.currency,
            }
            const setRequestPoint = (
                point,
                pointPlace,
                pointCityId,
                pointCountryId,
                pointHotelCode,
                pointHotelName,
                pointLatitude,
                pointLongitude,
                pointPlaceId,
                contentProviderId
            ) => {
                switch (point.type) {
                    case 'hotel':
                        query[pointPlace] = 'hotel'
                        query[pointCityId] = point.cityId
                        query[pointCountryId] = point.countryId
                        query[pointHotelCode] = point.hotelCode
                        query[pointHotelName] = point.name
                        query[contentProviderId] = point.contentProviderId
                        break
                    case 'city':
                        query[pointPlace] = 'hotel'
                        query[pointCityId] = point.id
                        query[pointCountryId] = point.countryId
                        break
                    case 'airport':
                        query[pointPlace] = point.iataCode
                        query[pointCityId] = point.cityId
                        query[pointCountryId] = point.countryId
                        break
                    case 'trainStation':
                        query[pointPlace] = point.id
                        query[pointCityId] = point.cityId
                        query[pointCountryId] = point.countryId
                        break
                    case 'cruisePort':
                        query[pointPlace] = point.id
                        query[pointCityId] = point.cityId
                        query[pointCountryId] = point.countryId
                        break
                    case 'position':
                        query[pointPlace] = 'position'
                        query[pointLatitude] = point.latitude.toFixed(6)
                        query[pointLongitude] = point.longitude.toFixed(6)
                        query[pointCityId] = point.city.id
                        query[pointCountryId] = point.city.countryId
                        query[pointPlaceId] = point.placeId
                }
            }
            setRequestPoint(
                this.arrivalPoint,
                'arrivalPlace',
                'arrivalCityId',
                'arrivalCountryId',
                'arrivalHotelCode',
                'arrivalHotelName',
                'arrivalLatitude',
                'arrivalLongitude',
                'arrivalPlaceId',
                'arrivalHotelContentProviderId'
            )
            setRequestPoint(
                this.departurePoint,
                'departurePlace',
                'departureCityId',
                'departureCountryId',
                'departureHotelCode',
                'departureHotelName',
                'departureLatitude',
                'departureLongitude',
                'departurePlaceId',
                'departureHotelContentProviderId'
            )

            if (persistentStore.selectedCompanyAndAgent) {
                query.clientId = persistentStore.selectedCompanyAndAgent.company.id

                if (persistentStore.selectedCompanyAndAgent.agent) {
                    query.agentId = persistentStore.selectedCompanyAndAgent.agent.personId
                }
            }

            return query
        }

        async search() {
            const query = this.newQuery()
            this.$emit('close-popup')
            if (this.arrivalPoint.hotelCode && !this.arrivalPoint.longitude) {
                try {
                    const {
                        hotelDescriptionInfo: {latitude, longitude, address},
                    } = await appInstance.$api.hotelInfo.get({hotelId: this.arrivalPoint.id})
                    Object.assign(this.arrivalPoint, {latitude, longitude, address: address?.addressLine})
                    // eslint-disable-next-line no-empty
                } catch (e) {}
            }
            if (this.departurePoint.hotelCode && !this.departurePoint.longitude) {
                try {
                    const {
                        hotelDescriptionInfo: {latitude, longitude, address},
                    } = await appInstance.$api.hotelInfo.get({hotelId: this.departurePoint.id})
                    Object.assign(this.departurePoint, {latitude, longitude, address: address?.addressLine})
                    // eslint-disable-next-line no-empty
                } catch (e) {}
            }
            transfersRuntimeStore.SET_ARRIVAL_POINT(this.arrivalPoint)
            transfersRuntimeStore.SET_DEPARTURE_POINT(this.departurePoint)
            const name = 'transfers'
            if (this.$route.name !== name) {
                transfersRuntimeStore.newSearch()
            }
            try {
                await this.$router.push({name, query})
            } catch (e) {
                await transfersRuntimeStore.search(transfersStore.searchRequest)
            }
            EventBus.$emit(CHANGE_TAB_MOBILE_EVENT, 1)
        }

        swapLocations() {
            const first = {...this.$refs.firstLocation.city}
            const second = {...this.$refs.secondLocation.city}
            this.departurePoint = Object.keys(second).length ? second : null
            this.arrivalPoint = Object.keys(first).length ? first : null
        }

        get isTO1() {
            return authStore.isTO1
        }

        get dateTimeData() {
            return this.$dateFns.parseISO(this.dateTime)
        }

        set dateTimeData(val) {
            this.dateTime = val ? this.$dateFns.format(val) : null
        }

        get minDateTime() {
            return this.$dateFns.format(new Date())
        }

        get formattedDate() {
            return this.dateTime !== null ? this.$filters.dateFormat(this.dateTime) : ''
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        checkVisibilitySpecifyExactAddressDialog(point) {
            return (
                this.$conf.public.showDialogToSpecifyExactAddressForTransfer &&
                point &&
                ['city', 'position'].includes(point.type)
            )
        }
    }

    export default TransferSearchForm
</script>

<style lang="scss" scoped>
    @use 'vuetify/settings';
    @use 'sass:map';

    @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
        .location-field {
            flex-basis: 100%;
            min-width: 0;
        }
    }
</style>
